import { MfsDataStore } from '../store/mfsDataStore';

import {
  throwIfEmptyOrTooLong,
  throwIfInvalidPropertyName,
  throwIfInvalidDisplayText,
  throwIfInvalidSecondaryLabels,
  throwIfInvalidDisplayTextInPatch,
  throwIfInvalidSecondaryLabelsInPatch,
  throwIfDeltaUpdatingSingleValueProperty,
  throwIfInvalidItemId,
} from './utility';
import { isMfsItem, MfsCollectionId, MfsItem, MfsItemId, MfsSubCollectionId } from '..';
import { mfsError } from './storeErrors';
import { throwIfHasSelfReference } from '../store/collections';
import { isMfsCollectionId } from '../store/mfsTypeGuard';

/** Validates the item id. If it exists check for empty or null or is a string */
export function validateItemId(_target: unknown, _input: string, descriptor: PropertyDescriptor): PropertyDescriptor {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (...args: unknown[]) {
    throwIfInvalidItemId('itemId', args[0]);
    return originalFunction.apply(this, args);
  };
  return descriptor;
}

/** Validates the item id. If it exists check for empty or null or is a string */
export function validateCollectionId(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsCollectionBasedMethod>
): TypedPropertyDescriptor<MfsCollectionBasedMethod> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (collectionId, ...args) {
    if (isMfsCollectionId(collectionId)) {
      throwIfInvalidItemId('collectionId', collectionId);
    } else {
      throwIfInvalidItemId('collectionId.id', collectionId.id);
      throwIfInvalidItemId('collectionId.parentId', collectionId.parentId);
    }

    return originalFunction.apply(this, [collectionId, ...args]);
  };

  return descriptor;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MfsPropertyBasedMethod = (itemId: MfsItemId, propertyName: string) => any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MfsCollectionBasedMethod = (collectionId: MfsCollectionId | MfsSubCollectionId, ...args: any[]) => void;

/**
 * Validates the item id and/or property name if it exists to check for empty or null or is a string. There's a
 * dependency on the order of arguments for any function that uses the decorator: 1) itemID 2) propertyName
 */
export function validatePropertyOperation(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsPropertyBasedMethod>
): TypedPropertyDescriptor<MfsPropertyBasedMethod> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (itemId, propertyName) {
    throwIfEmptyOrTooLong('itemId', itemId);
    throwIfInvalidPropertyName(propertyName);
    return originalFunction.apply(this, [itemId, propertyName]);
  };

  return descriptor;
}

/** Validates the propertyName */
export function validatePropertyName(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsDataStore['getMfsItemKey']>
): TypedPropertyDescriptor<MfsDataStore['getMfsItemKey']> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (propertyName) {
    throwIfInvalidPropertyName(propertyName);
    return originalFunction.apply(this, [propertyName]);
  };

  return descriptor;
}

/** Validates input arguments of {@link MfsDataStore.setItemProperty}. */
export function validateSetItemProperty(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsDataStore['setItemProperty']>
): TypedPropertyDescriptor<MfsDataStore['setItemProperty']> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (itemId, propertyName, value) {
    throwIfEmptyOrTooLong('itemId', itemId);
    throwIfInvalidPropertyName(propertyName);
    // ensure that common immutable properties are set to valid values, if any.
    throwIfDeltaUpdatingSingleValueProperty(propertyName, value);
    throwIfInvalidDisplayText(propertyName, value);
    throwIfInvalidSecondaryLabels(propertyName, value);
    return originalFunction.apply(this, [itemId, propertyName, value]);
  };

  return descriptor;
}

/** Validates input arguments of {@link MfsDataStore.patchItem}. */
export function validatePatchItem(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsDataStore['patchItem']>
): TypedPropertyDescriptor<MfsDataStore['patchItem']> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (itemId, patch) {
    // ensure that common immutable properties are set to valid values, if any.
    throwIfInvalidDisplayTextInPatch(patch);
    throwIfInvalidSecondaryLabelsInPatch(patch);
    return originalFunction.apply(this, [itemId, patch]);
  };

  return descriptor;
}

export function throwIfInvalidMfsItem(mfsItem: Partial<MfsItem>, select?: string[]): asserts mfsItem is MfsItem {
  if (!isMfsItem(mfsItem, select)) {
    throw mfsError.invalidItem();
  }
}

export function validateInsertItems(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsDataStore['insertItems']>
): TypedPropertyDescriptor<MfsDataStore['insertItems']> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (collectionId, itemIds, position) {
    throwIfHasSelfReference(collectionId, itemIds);
    return originalFunction.apply(this, [collectionId, itemIds, position]);
  };

  return descriptor;
}

export function validateInsertItem(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<MfsDataStore['insertItem']>
): TypedPropertyDescriptor<MfsDataStore['insertItem']> {
  const originalFunction = descriptor.value;

  if (!originalFunction) {
    return descriptor;
  }

  descriptor.value = function (collectionId, itemId, position) {
    throwIfHasSelfReference(collectionId, [itemId]);
    return originalFunction.apply(this, [collectionId, itemId, position]);
  };

  return descriptor;
}
